import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { trackPixel } from '@/lib/metaPixel';
import axios from 'axios';
import { User, Wrench, FileText, ChevronRight, ChevronLeft, Check, Phone, MapPin, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, label: 'About You', icon: User },
  { id: 2, label: 'Your Skills', icon: Wrench },
  { id: 3, label: 'Details', icon: FileText },
];

const SKILL_OPTIONS = [
  { id: 'basic_apps', label: 'Basic Online Apps', desc: 'WhatsApp, YouTube, Gmail, Google Maps' },
  { id: 'upi_payments', label: 'UPI & Digital Payments', desc: 'Google Pay, PhonePe, Paytm' },
  { id: 'video_editing', label: 'Video Editing & Media', desc: 'CapCut, InShot, Canva' },
  { id: 'social_media', label: 'Social Media', desc: 'Facebook, Instagram, YouTube' },
  { id: 'govt_services', label: 'Government Services', desc: 'Aadhaar, DigiLocker, IRCTC' },
  { id: 'online_shopping', label: 'Shopping & Delivery', desc: 'Amazon, Flipkart, Swiggy' },
  { id: 'health_apps', label: 'Health & Telemedicine', desc: 'Practo, PharmEasy' },
  { id: 'troubleshooting', label: 'Phone Troubleshooting', desc: 'Settings, Wi-Fi, storage' },
  { id: 'fraud_awareness', label: 'Fraud & Scam Awareness', desc: 'Phishing, OTP scams' },
  { id: 'teaching_patience', label: 'Patient Communication', desc: 'Working with elderly' },
];

const empty = {
  name: '', phone: '', city: '', age: '', email: '',
  qualification: '', skills: [], languages: '',
  availability: '', experience: '', why_join: ''
};

const DigiMitraApplyWizard = ({ open, onOpenChange }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ ...empty });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [waitlisted, setWaitlisted] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleSkill = (id) => set('skills', form.skills.includes(id) ? form.skills.filter(s => s !== id) : [...form.skills, id]);

  const canNext = () => {
    if (step === 1) return form.name.trim() && form.phone.trim() && form.city.trim();
    if (step === 2) return form.skills.length > 0;
    if (step === 3) return true;
    return false;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/digimitra/apply`, {
        ...form,
        age: form.age ? parseInt(form.age) : null,
        email: form.email || null,
      });
      if (res.data?.waitlisted) {
        setWaitlisted({ city: form.city.trim(), message: res.data.message });
        trackPixel('Lead', { content_name: 'DigiMitra Applicant Waitlist', content_category: 'DigiMitraJob', city: form.city.trim() });
      } else {
        setDone(true);
        trackPixel('Lead', { content_name: 'DigiMitra Applicant', content_category: 'DigiMitraJob', city: form.city.trim() });
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (val) => {
    if (!val) setTimeout(() => { setStep(1); setForm({ ...empty }); setDone(false); setWaitlisted(null); }, 300);
    onOpenChange(val);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="wiz-modal wiz-dm-apply" data-testid="dm-apply-modal">
        <VisuallyHidden.Root><DialogTitle>DigiMitra Application</DialogTitle><DialogDescription>Apply to become a DigiMitra associate in 3 steps.</DialogDescription></VisuallyHidden.Root>

        {waitlisted ? (
          <div className="wiz-success" data-testid="dm-apply-waitlist">
            <div style={{ fontSize: 48, marginBottom: 12 }}>🌱</div>
            <h2>Coming soon to {waitlisted.city}!</h2>
            <p style={{ maxWidth: 420, margin: '0 auto 12px' }}>{waitlisted.message}</p>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 18 }}>We've noted your interest to be a DigiMitra in {waitlisted.city} and will reach out on <strong>{form.phone}</strong> when we expand.</p>
            <Button className="wiz-done-btn wiz-dm-accent-bg" onClick={() => handleClose(false)} data-testid="dm-apply-waitlist-close">Got it</Button>
          </div>
        ) : done ? (
          <div className="wiz-success" data-testid="dm-apply-success">
            <div className="wiz-success-icon wiz-dm-accent"><Check size={40} strokeWidth={3} /></div>
            <h2>Application Received!</h2>
            <p>Thank you, <strong>{form.name}</strong>. We'll review your application and call you on <strong>{form.phone}</strong>.</p>
            <Button className="wiz-done-btn wiz-dm-accent-bg" onClick={() => handleClose(false)} data-testid="dm-apply-done-btn">Done</Button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="wiz-header wiz-dm-header">
              <h2>Become a DigiMitra</h2>
              <p>Help seniors navigate the digital world</p>
            </div>

            {/* Progress */}
            <div className="wiz-progress" data-testid="dma-progress">
              {STEPS.map((s) => {
                const Icon = s.icon;
                const active = step === s.id;
                const isDone = step > s.id;
                return (
                  <div key={s.id} className={`wiz-step ${active ? 'active' : ''} ${isDone ? 'done' : ''}`}>
                    <div className="wiz-step-dot wiz-dm-dot">
                      {isDone ? <Check size={16} strokeWidth={3} /> : <Icon size={16} />}
                    </div>
                    <span className="wiz-step-text">{s.label}</span>
                  </div>
                );
              })}
              <div className="wiz-bar"><div className="wiz-bar-fill wiz-dm-bar" style={{ width: `${((step - 1) / 2) * 100}%` }} /></div>
            </div>

            {/* Body */}
            <div className="wiz-body">
              {step === 1 && (
                <div className="wiz-fields wiz-anim" data-testid="dma-step-1">
                  <div className="wiz-row">
                    <div className="wiz-field"><label>Full Name *</label><Input data-testid="dma-name" placeholder="e.g. Priya Sharma" value={form.name} onChange={e => set('name', e.target.value)} autoFocus /></div>
                    <div className="wiz-field"><label>Phone *</label><Input data-testid="dma-phone" type="tel" placeholder="e.g. 98765 43210" value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
                  </div>
                  <div className="wiz-row">
                    <div className="wiz-field"><label><MapPin size={14} /> City *</label><Input data-testid="dma-city" placeholder="e.g. Delhi" value={form.city} onChange={e => set('city', e.target.value)} /></div>
                    <div className="wiz-field"><label>Age</label><Input data-testid="dma-age" type="number" placeholder="e.g. 28" value={form.age} onChange={e => set('age', e.target.value)} /></div>
                  </div>
                  <div className="wiz-field"><label>Qualification</label><Input data-testid="dma-qual" placeholder="e.g. B.Tech, BA, Diploma" value={form.qualification} onChange={e => set('qualification', e.target.value)} /></div>
                </div>
              )}

              {step === 2 && (
                <div className="wiz-fields wiz-anim" data-testid="dma-step-2">
                  <p className="wiz-skill-note">Select all skills you're comfortable with</p>
                  <div className="wiz-skill-grid">
                    {SKILL_OPTIONS.map(sk => (
                      <button type="button" key={sk.id} className={`wiz-skill-chip ${form.skills.includes(sk.id) ? 'selected' : ''}`} onClick={() => toggleSkill(sk.id)} data-testid={`dma-skill-${sk.id}`}>
                        <div className="wiz-skill-check">{form.skills.includes(sk.id) && <Check size={12} strokeWidth={3} />}</div>
                        <div><strong>{sk.label}</strong><span>{sk.desc}</span></div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="wiz-fields wiz-anim" data-testid="dma-step-3">
                  <div className="wiz-row">
                    <div className="wiz-field"><label>Email <span className="wiz-opt">optional</span></label><Input data-testid="dma-email" type="email" placeholder="e.g. priya@gmail.com" value={form.email} onChange={e => set('email', e.target.value)} autoFocus /></div>
                    <div className="wiz-field"><label>Languages Spoken</label><Input data-testid="dma-languages" placeholder="e.g. Hindi, English, Punjabi" value={form.languages} onChange={e => set('languages', e.target.value)} /></div>
                  </div>
                  <div className="wiz-field"><label>Availability</label><Input data-testid="dma-availability" placeholder="e.g. Weekdays, Full-time, Part-time" value={form.availability} onChange={e => set('availability', e.target.value)} /></div>
                  <div className="wiz-field"><label>Prior experience with seniors</label><Input data-testid="dma-experience" placeholder="e.g. 2 years at a senior center" value={form.experience} onChange={e => set('experience', e.target.value)} /></div>
                  <div className="wiz-field"><label>Why do you want to become a DigiMitra?</label><Textarea data-testid="dma-why" placeholder="Tell us your motivation..." value={form.why_join} onChange={e => set('why_join', e.target.value)} rows={2} /></div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="wiz-footer">
              {step > 1 && <Button variant="ghost" className="wiz-back" onClick={() => setStep(step - 1)} data-testid="dma-back"><ChevronLeft size={18} /> Back</Button>}
              <div className="wiz-spacer" />
              {step < 3 ? (
                <Button className="wiz-next wiz-dm-accent-bg" disabled={!canNext()} onClick={() => setStep(step + 1)} data-testid="dma-next">Next <ChevronRight size={18} /></Button>
              ) : (
                <Button className="wiz-next wiz-dm-accent-bg" disabled={loading} onClick={handleSubmit} data-testid="dma-submit">
                  {loading ? <><Loader2 size={18} className="dm-spin" /> Submitting...</> : <>Submit Application <Check size={18} /></>}
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DigiMitraApplyWizard;
